import { Component, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { LoggerService } from "@bequeathed/ngx-app-core";
import { InterviewState, Question, QuestionGroup } from "@bequeathed/ngx-services";
import { BaseForm } from "../../base-form/base-form.component";
import { BaseControl } from "../base-control/base-control.component";
import { CheckboxControl } from "../checkbox-control/checkbox-control.component";
import { ConfirmOptionControl } from "../confirm-option-control/confirm-option-control.component";
import { CurrencyControl } from "../currency-control/currency-control.component";
import { DateControl } from "../date-control/date-control.component";
import { DisplayControl } from "../display-control/display-control.component";
import { DropdownControl } from "../dropdown-control/dropdown-control.component";
import { EmailControl } from "../email-control/email-control.component";
import { NumberControl } from "../number-control/number-control.component";
import { PercentageControl } from "../percentage-control/percentage-control.component";
import { PostcodeControl } from "../postcode-control/postcode-control.component";
import { RadioButtonControl } from "../radio-button-control/radio-button-control.component";
import { TextControl } from "../text-control/text-control.component";

const LOG: LoggerService = LoggerService.get('GroupControl');

class GroupAndQuestionMap {
    originalIndex: number | undefined;
    index: number;
    group: QuestionGroup;
    controls: BaseControl[];

    constructor(originalIndex: number | undefined, index: number, group: QuestionGroup, controls: BaseControl[]) {
        this.originalIndex = originalIndex;
        this.index = index;
        this.group = group;
        this.controls = controls;
    }
}

/**
 * Represents a group of questions displayed together on a single page. These controls are also typically grouped
 * together under a "group" using the following syntax:
 *
 * {group question.id}[$index].{sub question.id}
 *
 * @author Dan Bennett (dbennett)
 */
@Component({
    standalone: true,
    selector: 'group-control',
    templateUrl: './group-control.component.html',
    styleUrl: './group-control.component.sass',
    imports: [
        // Angular
        ReactiveFormsModule,
        // Bequeathed
        CheckboxControl,
        ConfirmOptionControl,
        CurrencyControl,
        DisplayControl,
        DropdownControl,
        EmailControl,
        NumberControl,
        PercentageControl,
        PostcodeControl,
        RadioButtonControl,
        TextControl,
        DateControl
    ],
     encapsulation: ViewEncapsulation.None
})
export class GroupControl extends BaseControl implements OnInit {

    @Input() groupName: string | undefined;
    // @ts-expect-error This will not be null
    @Input() baseForm: BaseForm;
    // @ts-expect-error This will not be null
    @Input() form: UntypedFormGroup;
    // @ts-expect-error This will not be null
    @Input() onEnter: ((event: any) => void);

    groups: QuestionGroup[] = [];

    @ViewChild("changedMindControl") changedMindControl: BaseControl | undefined;

    override ngOnInit() {
        this.baseForm.questionControl = this;
        this.baseForm.questionControls = []; // reset question controls from previous group question

        this.groups = [];

        if (this.question.source || (this.question.groupedQuestions?.size ?? 0) > 0) {
            this.question.groupedQuestions?.forEach((questions: Question[], index: number) => {
                const filteredQuestions = questions.filter((q: Question) =>
                    !q.id?.endsWith('changed_mind')
                );

                this.groups.push(new QuestionGroup({
                    id: this.question.id,
                    name: filteredQuestions.find((q: Question) => q.type === 'DISPLAY')?.userResponse,
                    questions: filteredQuestions,
                    index: index,
                    adding: false,
                    editing: false,
                    locked: false,
                }))
            });

            LOG.debug('init', `Created ${this.groups.length} groups`);
        } else {
            this.addGroup();
        }
    }

    get adding(): boolean {
        return this.groups.filter(g => g.adding).length > 0;
    }

    get editing(): boolean {
        return this.groups.filter(g => g.editing).length > 0;
    }


    addGroup(): void {
        let questions: Question[];
        if (this.editingResponse) {
            questions = this.question.groupedQuestions!
                .get(0)!
                .map(question => {
                    const clone: Question = structuredClone(question);
                    clone.id = clone.id?.replace(/\[[0-9]*]/, `[${this.groups.length}]`)
                    clone.userResponse = undefined;
                    clone.calculatedResponse = undefined;
                    return clone;
                })
        } else {
            questions = this.question.questions!
                .filter(question => !['METADATA', 'HIDDEN'].includes(question.type!))
                .map(question => structuredClone(question))
        }

        const filteredQuestions = questions.filter((q: Question) =>
            !q.id?.endsWith('changed_mind')
        );

        this.groups.push(new QuestionGroup({
            questions: filteredQuestions,
            index: this.nextIndex(),
            adding: true,
            editing: false,
            locked: false,
        }));
    }

    nextIndex(index: number = this.groups.length): number {
        if (this.groups.some((group: QuestionGroup) => group.index === index)) {
            return this.nextIndex(index + 1)
        }
        return index
    }

    async save(group: QuestionGroup, lockGroup = true): Promise<boolean> {
        const controls: BaseControl[] = this.baseForm.questionControls.filter(q => q.question.id!.startsWith(`${this.question.id}[${group.index}]`));

        let errored = false;
        for (const c of controls) {
            await c.validate();
            if (c.controlHasError()) {
                errored = true;
            }
        }
        if (errored) {
            return false;
        }

        LOG.debug('save', `Saving group: ${group.name}[${group.index}]`);
        controls.forEach((control: BaseControl) => {
            const filtered = group.questions!
                .filter((q: Question) => {
                    return q.id === control.question.id
                })[0]

            filtered
                .calculatedResponse = control.modelValue
        });

        if (lockGroup) {
            group.locked = true;
            group.adding = false;
            group.editing = false;
        }

        return true;
    }

    cancel(group: QuestionGroup): void {
        if (!group.editing && !group.locked) {
            this.groups.pop();
        } else {
            group.editing = false;
            group.locked = true;
        }
    }

    delete(group: QuestionGroup): void {

        this.question.groupedQuestions?.delete(group.index!)
        for (let i = 0; i < this.groups.length; i++) {
            if (group.index === this.groups[i].index) {
                this.groups.splice(i, 1);
                break;
            }
        }

        // Remove any related controls etc
        const groupName = `${group.name}[${group.index}]`;
        for (let i = 0; i < this.baseForm.questionControls.length; i++) {
            const cntrl: BaseControl = this.baseForm.questionControls[i];
            if (cntrl.question.id!.indexOf(groupName) === 0) {
                this.baseForm.questionControls.splice(i, 1);
                i--;
            }
        }
    }

    saveAndContinue(event: any): void {
        this.groups.forEach((group: QuestionGroup) => this.save(group, false));

        this.onEnter(event);
    }

    override applyValue(state: InterviewState): void {
        if (this.changedMindControl?.control.value[0]) {
            // Handle changed_mind checkbox question by swapping current question
            const currentQuestion = state.currentQuestion;
            // @ts-expect-error only after change_mind question
            state.currentQuestion = currentQuestion!.questions!.find((q: Question) => {
                if (q.id?.endsWith('changed_mind')) {
                    q.userResponse = this.changedMindControl!.question.acceptedValues.keys().next().value;
                    return q
                }
            })
        } else {
            if (!this.question.source && (!this.editingResponse || this.question.groupedQuestions!.size < this.groups.length)) {
                // Reset indexes so they're consecutive from 0
                let index = 0;
                const questionAndGroupMaps: GroupAndQuestionMap[] = [];
                for (const group of this.groups) {
                    const map = new GroupAndQuestionMap(group.index, index, group, []);
                    this.baseForm.questionControls
                        .filter((control: BaseControl) => control.groupIndex === group.index)
                        .forEach((control: BaseControl) => map.controls.push(control));
                    questionAndGroupMaps.push(map);
                    index++;
                }
                questionAndGroupMaps.forEach((group) => {
                    group.group.index = group.index;
                    group.controls.forEach((control: BaseControl) => {control.groupIndex = group.index});
                    this.addGroupedQuestions(group.group)
                });
            }
            this.groups.forEach((group: QuestionGroup) => {
                this.baseForm.questionControls
                    .filter((control: BaseControl) => control.groupIndex === group.index)
                    .forEach((control: BaseControl) => {
                        const question = group.questions!.filter((q: Question) => q.id === control.questionId)[0];
                        control.applyValue(question);
                    });
            });
            state.currentQuestion!.groupedQuestions = this.question.groupedQuestions;
            LOG.debug('applyValue', `Applied all values from group into state`);
        }
    }


    addGroupedQuestions(group: QuestionGroup): void {

        // ToDo :: Check that indexes are corrected before save
        for (const subQuestion of group.questions!) {
            const id = subQuestion.id?.includes('[') ? subQuestion.id : `${this.question.id}[${group.index}].${subQuestion.id}`;
            if (subQuestion.type !== 'HIDDEN' && subQuestion.type !== 'DISPLAY' && subQuestion.type !== 'METADATA') {
                const control = this.baseForm.questionControls
                    .find(control => control.groupIndex === group.index && control.questionId === subQuestion.id);
                control!.questionId = id;
            }
            subQuestion.id = id;
        }
        if (!this.question.groupedQuestions) { // Grouped questions not set, setting an empty map
            this.question.groupedQuestions = new Map<number, Question[]>()
        }
        this.question.groupedQuestions!.set(group.index!, group.questions!)
    }

    destroy(): void {
        this.groups = [];
    }

    override setPreviousResponse() {
        this.question.groupedQuestions?.forEach((groupQuestions, groupId) => {
            this.groups.push(new QuestionGroup({
                id: this.question.id,
                name: groupQuestions.find((q: Question) => q.type === 'DISPLAY')?.calculatedResponse,
                questions: groupQuestions,
                index: groupId,
                adding: false,
                editing: true,
                locked: false,
            }))

        })
    }

    /*
     * Get the changed_mind question if it exists. This is used to swap the current question with the changed_mind question
     * when the user has answered "yes" to the changed_mind question.
     */
    get changedMindQuestion() {
        return this.question.questions?.find(q => q.id!.endsWith('changed_mind'));
    }

    /**
     * Returns deduplicated validation messages from the AI validation errors.
     * Multiple fields can fail with the same message (e.g. minimum gift amount), so
     * we show each unique message only once.
     */
    get uniqueValidationMessages(): string[] {
        const validations = this.baseForm.interviewState()?.errors?.validations;
        if (!validations) return [];
        return [...new Set(Object.values(validations).filter((v): v is string => !!v))];
    }
}
